import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { messagingApi, type DLQMessage, type MessagingDashboard, type ReplayJob, type RetryPolicy } from '../../api/messaging';

export function MessagingReliabilityPage() {
  const [dashboard, setDashboard] = useState<MessagingDashboard | null>(null);
  const [messages, setMessages] = useState<DLQMessage[]>([]);
  const [jobs, setJobs] = useState<ReplayJob[]>([]);
  const [policies, setPolicies] = useState<RetryPolicy[]>([]);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');

  async function refresh() {
    try {
      const [dash, dlq, replayJobs, retry] = await Promise.all([
        messagingApi.dashboard(),
        messagingApi.listDLQ({ limit: 50 }),
        messagingApi.replayJobs(),
        messagingApi.retryPolicies(),
      ]);
      setDashboard(dash);
      setMessages(dlq.messages ?? []);
      setJobs(replayJobs.jobs ?? []);
      setPolicies(retry.policies ?? []);
    } catch (e) {
      setError(String(e));
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function replay(id: string, dryRun: boolean) {
    setStatus('');
    try {
      const res = await messagingApi.replayOne(id, dryRun);
      setStatus(dryRun ? `Preview: ${res.data?.total_count ?? 0} message(s)` : `Replayed ${res.data?.replayed ?? 0}`);
      await refresh();
    } catch (e) {
      setError(String(e));
    }
  }

  const cats = dashboard?.failure_by_category ?? {};

  return (
    <div className="mx-auto max-w-7xl p-6">
      <Link to="/events" className="text-sm text-blue-600 hover:underline dark:text-blue-400">← Event Hub</Link>
      <h1 className="mt-2 text-2xl font-semibold">Messaging Reliability</h1>
      <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
        DEV-000060 — enterprise DLQ, replay engine, retry policies and recovery console.
      </p>
      {error && <p className="mt-4 text-sm text-red-600 dark:text-red-400">{error}</p>}
      {status && <p className="mt-4 text-sm text-green-600 dark:text-green-400">{status}</p>}

      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {[
          ['DLQ Total', dashboard?.dlq_total ?? 0],
          ['DLQ Pending', dashboard?.dlq_pending ?? 0],
          ['Active Replays', dashboard?.replay_jobs_active ?? 0],
          ['Retries', dashboard?.retry_total ?? 0],
          ['Recovery', dashboard?.recovery_pending ?? 0],
        ].map(([label, val]) => (
          <div key={String(label)} className="rounded-xl border p-4 dark:border-slate-700">
            <p className="text-sm text-slate-500">{label}</p>
            <p className="text-2xl font-semibold">{val}</p>
          </div>
        ))}
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-3">
        <section className="rounded-xl border dark:border-slate-700 lg:col-span-2">
          <div className="border-b px-4 py-3 dark:border-slate-700">
            <h2 className="font-medium">Dead Letter Center</h2>
          </div>
          <ul className="divide-y dark:divide-slate-700">
            {messages.length === 0 && <li className="px-4 py-6 text-sm text-slate-500">No DLQ messages.</li>}
            {messages.map((m) => (
              <li key={m.id} className="flex items-center justify-between gap-4 px-4 py-3">
                <div>
                  <p className="font-mono text-sm">{m.event_name}</p>
                  <p className="text-xs text-slate-500">{m.failure_category} — {m.failure_reason.slice(0, 80)}</p>
                </div>
                <div className="flex gap-2">
                  <button type="button" onClick={() => void replay(m.id, true)}
                    className="rounded border px-2 py-1 text-xs dark:border-slate-600">Preview</button>
                  {m.replay_eligible && (
                    <button type="button" onClick={() => void replay(m.id, false)}
                      className="rounded bg-blue-600 px-2 py-1 text-xs text-white">Replay</button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </section>

        <section className="space-y-6">
          <div className="rounded-xl border dark:border-slate-700">
            <div className="border-b px-4 py-3 dark:border-slate-700"><h2 className="font-medium">Failure Analytics</h2></div>
            <ul className="p-4 space-y-1 text-sm">
              {Object.entries(cats).map(([k, v]) => (
                <li key={k} className="flex justify-between"><span>{k}</span><span>{v}</span></li>
              ))}
            </ul>
          </div>
          <div className="rounded-xl border dark:border-slate-700">
            <div className="border-b px-4 py-3 dark:border-slate-700"><h2 className="font-medium">Retry Policies</h2></div>
            <ul className="p-4 space-y-2 text-sm">
              {policies.map((p) => (
                <li key={p.id}>{p.name} — {p.strategy} (max {p.max_attempts})</li>
              ))}
            </ul>
          </div>
          <div className="rounded-xl border dark:border-slate-700">
            <div className="border-b px-4 py-3 dark:border-slate-700"><h2 className="font-medium">Replay Jobs</h2></div>
            <ul className="p-4 space-y-1 text-xs font-mono">
              {jobs.slice(0, 5).map((j) => (
                <li key={j.id}>{j.id.slice(0, 8)}… {j.status} +{j.replayed}/-{j.failed}</li>
              ))}
            </ul>
          </div>
        </section>
      </div>
    </div>
  );
}
